import { useEffect, useState } from 'react';
import { HeaderContent, HEADER_TITLE_CLASS } from '../components/shell/app-header';
import { Modal, FormField, SelectField } from '../components/modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Handshake, MagnifyingGlass, PencilSimple } from '@phosphor-icons/react';
import {
  getMySchoolId, listClassrooms, listSchoolStudentsPaginated, addStudent, inviteParentOrTutor,
  renameStudent, updateStudentNotes,
  listParentInvitations, resendParentInvite,
  type Classroom, type Student, type ParentInvitation,
} from '../lib/direction';
const STATUS_LABEL: Record<string, string> = { pending: 'En attente', accepted: 'Accepté', revoked: 'Révoqué' };

const PAGE_SIZE = 20;

const listShellClass = "bg-[var(--naia-card)] border border-[var(--naia-line)] rounded-[var(--naia-r-card)] shadow-[var(--naia-shadow-card)] overflow-hidden divide-y divide-[var(--naia-line)]";

export default function DirectionParentsEleves() {
  const [schoolId, setSchoolId] = useState<string | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [studentsTotal, setStudentsTotal] = useState(0);
  const [classes, setClasses] = useState<Classroom[]>([]);
  const [invites, setInvites] = useState<ParentInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  const [modal, setModal] = useState(false);
  const [cFirst, setCFirst] = useState(''); const [cLast, setCLast] = useState('');
  const [cClass, setCClass] = useState(''); const [cEmail, setCEmail] = useState('');
  const [inviteFor, setInviteFor] = useState<Student | null>(null); const [inviteEmail, setInviteEmail] = useState('');
  const [renameFor, setRenameFor] = useState<Student | null>(null); const [rFirst, setRFirst] = useState(''); const [rLast, setRLast] = useState('');
  const [notesFor, setNotesFor] = useState<Student | null>(null); const [notesText, setNotesText] = useState('');

  const reloadStudents = async (nextPage = page, nextSearch = search) => {
    setStudentsLoading(true);
    try {
      const { students: st, total } = await listSchoolStudentsPaginated(nextPage, PAGE_SIZE, nextSearch.trim() || undefined);
      setStudents(st); setStudentsTotal(total);
    } catch {
      setStudents([]); setStudentsTotal(0);
    } finally { setStudentsLoading(false); }
  };

  const reload = async () => {
    try {
      const sid = await getMySchoolId();
      setSchoolId(sid);
      const [cl, inv] = await Promise.all([listClassrooms(sid ?? ''), listParentInvitations()]);
      setClasses(cl); setInvites(inv);
      await reloadStudents(0, search);
    } catch {
      setClasses([]);
      setInvites([]);
    } finally { setLoading(false); }
  };
  /* eslint-disable-next-line react-hooks/exhaustive-deps -- reload stable, run once on mount */
  useEffect(() => { void reload(); }, []);

  const totalPages = Math.max(1, Math.ceil(studentsTotal / PAGE_SIZE));
  const classNameOf = (id: string) => classes.find((c) => c.id === id)?.name ?? '—';

  const onSearch = (v: string) => {
    setSearch(v);
    setPage(0);
    void reloadStudents(0, v);
  };

  const goToPage = (p: number) => {
    const next = Math.max(0, Math.min(totalPages - 1, p));
    setPage(next);
    void reloadStudents(next, search);
  };

  const submitCreate = async () => {
    if (!schoolId || !cClass || (!cFirst.trim() && !cLast.trim()) || saving) return;
    setSaving(true); setNotice(null);
    try {
      const s = await addStudent({ classroomId: cClass, schoolId, firstName: cFirst, lastName: cLast });
      if (cEmail.trim()) await inviteParentOrTutor(cEmail.trim(), [s.id]);
      setModal(false); setCFirst(''); setCLast(''); setCClass(''); setCEmail('');
      await reload();
    } catch (e) { setNotice(e instanceof Error ? e.message : String(e)); }
    finally { setSaving(false); }
  };

  const submitInvite = async () => {
    if (!inviteFor || !inviteEmail.trim() || saving) return;
    setSaving(true); setNotice(null);
    try {
      await inviteParentOrTutor(inviteEmail.trim(), [inviteFor.id]);
      setInviteFor(null); setInviteEmail('');
      await reload();
    } catch (e) { setNotice(e instanceof Error ? e.message : String(e)); }
    finally { setSaving(false); }
  };

  const openRename = (s: Student) => { setRenameFor(s); setRFirst(s.first_name); setRLast(s.last_name); };
  const submitRename = async () => {
    if (!renameFor || (!rFirst.trim() && !rLast.trim()) || saving) return;
    setSaving(true); setNotice(null);
    try {
      await renameStudent(renameFor.id, rFirst.trim(), rLast.trim());
      setRenameFor(null);
      await reloadStudents();
    } catch (e) { setNotice(e instanceof Error ? e.message : String(e)); }
    finally { setSaving(false); }
  };

  const openNotes = (s: Student) => { setNotesFor(s); setNotesText(s.notes ?? ''); };
  const submitNotes = async () => {
    if (!notesFor || saving) return;
    setSaving(true); setNotice(null);
    try {
      await updateStudentNotes(notesFor.id, notesText.trim() || null);
      setNotesFor(null);
      await reloadStudents();
    } catch (e) { setNotice(e instanceof Error ? e.message : String(e)); }
    finally { setSaving(false); }
  };

  const onResend = async (inv: ParentInvitation) => {
    setNotice(null);
    try {
      await resendParentInvite(inv.id);
      setNotice(`Invitation renvoyée à ${inv.email}.`);
    } catch (e) { setNotice(e instanceof Error ? e.message : String(e)); }
  };

  return (
    <>
      <HeaderContent
        width="md"
        showBurger
        eyebrow="Mode direction"
        eyebrowIcon={<Handshake size={11} weight="bold" />}
        actions={<Button onClick={() => setModal(true)}><span className="text-base leading-none">+</span> Élève (+ parent)</Button>}
      >
        <div className="min-w-0">
          <h1 className={HEADER_TITLE_CLASS}>Parents & Élèves</h1>
          <p className="m-0 mt-0.5 text-[13px] sm:text-[13.5px] leading-snug text-muted-foreground">
            Élèves de l'établissement, liens responsables et invitations en cours.
          </p>
        </div>
      </HeaderContent>
      <main className="max-w-[880px] mx-auto px-5 pb-16 pt-4">

        {notice && (
          <p className="mb-4 px-[14px] py-[10px] rounded-[10px] bg-primary/10 text-primary text-sm font-semibold">
            {notice}
          </p>
        )}

        {loading ? (
          <p className="text-[var(--naia-text-secondary)] py-4">Chargement…</p>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[10.5px] font-extrabold tracking-[.12em] uppercase text-[var(--naia-text-secondary)]">Élèves</span>
              {studentsTotal > 0 && (
                <span className="text-[10px] font-black min-w-[18px] h-[18px] px-1.5 rounded-full flex items-center justify-center text-primary bg-primary/10">
                  {studentsTotal}
                </span>
              )}
            </div>

            <div className="relative mb-3">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--naia-text-secondary)]" />
              <Input value={search} onChange={(e) => onSearch(e.target.value)} placeholder="Rechercher un élève (nom, prénom)…" className="pl-8 text-[12.5px]" />
            </div>

            {studentsLoading ? (
              <p className="text-[var(--naia-text-secondary)] text-[13.5px] mb-8">Chargement…</p>
            ) : students.length === 0 ? (
              <p className="text-[var(--naia-text-secondary)] text-[13.5px] mb-8">Aucun élève.</p>
            ) : (
              <div className={`${listShellClass} mb-3`}>
                {students.map((s) => (
                  <div key={s.id} className="flex items-center gap-3 px-4 py-[13px] hover:bg-accent transition-colors">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <div className="font-bold text-[var(--naia-text-primary)]">{s.first_name} {s.last_name}{!s.active && <span className="text-[var(--naia-text-secondary)] font-normal"> · inactif</span>}</div>
                        <button onClick={() => openRename(s)} aria-label="Renommer" className="text-[var(--naia-text-secondary)] hover:text-[var(--naia-text-primary)]">
                          <PencilSimple size={13} />
                        </button>
                      </div>
                      <div className="text-[13px] text-[var(--naia-text-secondary)] mt-0.5">{classNameOf(s.classroom_id)}</div>
                      {s.notes ? (
                        <button onClick={() => openNotes(s)} className="text-left text-[12px] text-[var(--naia-text-secondary)] italic mt-1 hover:underline">
                          {s.notes}
                        </button>
                      ) : (
                        <button onClick={() => openNotes(s)} className="text-left text-[12px] text-primary mt-1 hover:underline">
                          + Ajouter une note privée
                        </button>
                      )}
                    </div>
                    <Button variant="secondary" size="sm" onClick={() => { setInviteFor(s); setInviteEmail(''); }}>Inviter un parent / tuteur</Button>
                  </div>
                ))}
              </div>
            )}

            {totalPages > 1 && (
              <div className="flex items-center justify-between mb-8 text-[12px]">
                <span className="text-[var(--naia-text-secondary)]">
                  Page {page + 1} / {totalPages} — {studentsTotal} au total
                </span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => goToPage(page - 1)} disabled={page === 0}>Précédent</Button>
                  <Button variant="outline" size="sm" onClick={() => goToPage(page + 1)} disabled={page >= totalPages - 1}>Suivant</Button>
                </div>
              </div>
            )}

            <div className="flex items-center gap-2 mb-3">
              <span className="text-[10.5px] font-extrabold tracking-[.12em] uppercase text-[var(--naia-text-secondary)]">Invitations parent / tuteur</span>
              {invites.length > 0 && (
                <span className="text-[10px] font-black min-w-[18px] h-[18px] px-1.5 rounded-full flex items-center justify-center text-primary bg-primary/10">
                  {invites.length}
                </span>
              )}
            </div>
            {invites.length === 0 ? (
              <p className="text-[var(--naia-text-secondary)] text-[13.5px]">Aucune invitation envoyée.</p>
            ) : (
              <div className={listShellClass}>
                {invites.map((inv) => (
                  <div key={inv.id} className="flex items-center gap-3 px-4 py-[13px]">
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-[var(--naia-text-primary)]">{inv.email}</div>
                      <div className="text-[13px] text-[var(--naia-text-secondary)] mt-0.5">{inv.student_ids.length} élève(s)</div>
                    </div>
                    <span className="text-[12px] font-bold text-primary bg-primary/10 px-2.5 py-[5px] rounded-full shrink-0">
                      {STATUS_LABEL[inv.status] ?? inv.status}
                    </span>
                    {inv.status === 'pending' && (
                      <Button variant="secondary" size="sm" onClick={() => void onResend(inv)}>Renvoyer</Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>

      <Modal open={modal} title="Ajouter un élève + inviter son parent / tuteur" onClose={() => setModal(false)}
        footer={<><Button variant="secondary" onClick={() => setModal(false)}>Annuler</Button><Button onClick={submitCreate} disabled={saving || !cClass || (!cFirst.trim() && !cLast.trim())}>{saving ? '…' : 'Créer'}</Button></>}>
        <FormField label="Prénom" value={cFirst} onChange={setCFirst} autoFocus placeholder="Prénom" />
        <FormField label="Nom" value={cLast} onChange={setCLast} placeholder="Nom" />
        <SelectField label="Classe" value={cClass} onChange={setCClass} options={classes.map((c) => ({ value: c.id, label: c.name }))} placeholder="Choisir une classe…" />
        <FormField label="Email du parent / tuteur (optionnel)" value={cEmail} onChange={setCEmail} placeholder="email" type="email" />
      </Modal>

      <Modal open={Boolean(inviteFor)} title={`Inviter un parent / tuteur${inviteFor ? ` — ${inviteFor.first_name} ${inviteFor.last_name}` : ''}`} onClose={() => setInviteFor(null)}
        footer={<><Button variant="secondary" onClick={() => setInviteFor(null)}>Annuler</Button><Button onClick={submitInvite} disabled={saving || !inviteEmail.trim()}>{saving ? '…' : 'Envoyer'}</Button></>}>
        <FormField label="Email du parent / tuteur" value={inviteEmail} onChange={setInviteEmail} autoFocus placeholder="email" type="email" onEnter={submitInvite} />
      </Modal>

      <Modal open={Boolean(renameFor)} title="Renommer l'élève" onClose={() => setRenameFor(null)}
        footer={<><Button variant="secondary" onClick={() => setRenameFor(null)}>Annuler</Button><Button onClick={submitRename} disabled={saving || (!rFirst.trim() && !rLast.trim())}>{saving ? '…' : 'Enregistrer'}</Button></>}>
        <FormField label="Prénom" value={rFirst} onChange={setRFirst} autoFocus placeholder="Prénom" />
        <FormField label="Nom" value={rLast} onChange={setRLast} placeholder="Nom" onEnter={submitRename} />
      </Modal>

      <Modal open={Boolean(notesFor)} title={`Note privée${notesFor ? ` — ${notesFor.first_name} ${notesFor.last_name}` : ''}`} onClose={() => setNotesFor(null)}
        footer={<><Button variant="secondary" onClick={() => setNotesFor(null)}>Annuler</Button><Button onClick={submitNotes} disabled={saving}>{saving ? '…' : 'Enregistrer'}</Button></>}>
        <label className="block">
          <div className="text-[12px] font-bold text-muted-foreground mb-[6px]">Note (visible uniquement par la direction)</div>
          <textarea
            value={notesText}
            autoFocus
            onChange={(e) => setNotesText(e.target.value)}
            placeholder="Allergie, suivi particulier, information utile…"
            rows={4}
            className="w-full px-[14px] py-[11px] box-border border border-border rounded-sm bg-naia-card-alt text-foreground text-[15px] font-[inherit] resize-none"
          />
        </label>
      </Modal>
    </>
  );
}
